import { colors } from "../../colors";
import type { ButtonSize, ButtonStyleProps, ButtonVariant } from "./types";

// Default colors
const DEFAULT_COLORS = {
  white: colors.white,
  gray100: colors.gray[100],
  gray300: colors.gray[300],
  gray600: colors.gray[600],
  transparent: "transparent",
};

// Default size styles
export const getButtonSizeStyles = (size: ButtonSize): string => {
  switch (size) {
    case "small":
      return `
        padding: 6px 12px;
        font-size: 1em;
        line-height: 20px;
      `;
    case "large":
      return `
        padding: 11px 26px;
        font-size: 1.35em;
        line-height: 28px;
      `;
    case "medium":
    default:
      return `
        padding: 9px 18px;
        font-size: 1.1em;
        line-height: 24px;
      `;
  }
};

// Default variant styles
export const getButtonVariantStyles = (
  variant: ButtonVariant,
  styleProps?: ButtonStyleProps,
): string => {
  const {
    backgroundColor,
    hoverBackgroundColor,
    activeBackgroundColor,
    color,
    hoverColor,
    activeColor,
    borderColor,
    hoverBorderColor,
    activeBorderColor,
  } = styleProps ?? {};

  switch (variant) {
    case "solid":
      return `
        background-color: ${backgroundColor ?? colors.primary[300]};
        color: ${color ?? DEFAULT_COLORS.white};
        border: 1px solid ${borderColor ?? backgroundColor ?? colors.primary[300]};
        
        &:hover:not(:disabled) {
          background-color: ${hoverBackgroundColor ?? colors.primary[500]};
          color: ${hoverColor ?? DEFAULT_COLORS.white};
          border-color: ${hoverBorderColor ?? hoverBackgroundColor ?? colors.primary[500]};
        }
        
        &:active:not(:disabled) {
          background-color: ${activeBackgroundColor ?? "#4338ca"};
          color: ${activeColor ?? DEFAULT_COLORS.white};
          border-color: ${activeBorderColor ?? activeBackgroundColor ?? "#4338ca"};
        }
      `;
    case "outline":
      return `
        background-color: ${backgroundColor ?? DEFAULT_COLORS.transparent};
        color: ${color ?? colors.primary[300]};
        border: 1px solid ${borderColor ?? color ?? colors.primary[300]};
        
        &:hover:not(:disabled) {
          background-color: ${hoverBackgroundColor ?? colors.primary[300]};
          color: ${hoverColor ?? DEFAULT_COLORS.white};
          border-color: ${hoverBorderColor ?? colors.primary[300]};
        }
        
        &:active:not(:disabled) {
          background-color: ${activeBackgroundColor ?? "#4338ca"};
          color: ${activeColor ?? DEFAULT_COLORS.white};
          border-color: ${activeBorderColor ?? "#4338ca"};
        }
      `;
    case "ghost":
      return `
        background-color: ${backgroundColor ?? DEFAULT_COLORS.transparent};
        color: ${color ?? DEFAULT_COLORS.gray600};
        border: 1px solid ${borderColor ?? DEFAULT_COLORS.transparent};
        
        &:hover:not(:disabled) {
          background-color: ${hoverBackgroundColor ?? DEFAULT_COLORS.gray100};
          color: ${hoverColor ?? DEFAULT_COLORS.gray600};
          border-color: ${hoverBorderColor ?? DEFAULT_COLORS.transparent};
        }
        
        &:active:not(:disabled) {
          background-color: ${activeBackgroundColor ?? DEFAULT_COLORS.gray300};
          color: ${activeColor ?? DEFAULT_COLORS.gray600};
          border-color: ${activeBorderColor ?? DEFAULT_COLORS.transparent};
        }
      `;
    case "link":
      return `
        background-color: ${backgroundColor ?? DEFAULT_COLORS.transparent};
        color: ${color ?? colors.primary[300]};
        border: 1px solid ${borderColor ?? DEFAULT_COLORS.transparent};
        text-decoration: underline;
        padding: 0;
        
        &:hover:not(:disabled) {
          background-color: ${hoverBackgroundColor ?? DEFAULT_COLORS.transparent};
          color: ${hoverColor ?? colors.primary[500]};
          border-color: ${hoverBorderColor ?? DEFAULT_COLORS.transparent};
          text-decoration: none;
        }
        
        &:active:not(:disabled) {
          background-color: ${activeBackgroundColor ?? DEFAULT_COLORS.transparent};
          color: ${activeColor ?? "#4338ca"};
          border-color: ${activeBorderColor ?? DEFAULT_COLORS.transparent};
          text-decoration: none;
        }
      `;
    default:
      return "";
  }
};
